/**
 * Fee breakdown for a sample order (#777): the calculator's buy, then either selling it later or holding it to a
 * winning settlement. All fee maths is trading/fees.ts (outcomeFee); trade values come from the ticket's economics.
 * The builder fee (#770) applies to the buy and the sell (not settlement); it is 0 unless a builder is configured.
 */
import { useId, useState } from 'react';
import type { Side } from '../hl/outcomes';
import { formatShares, formatUsd, pxToCents } from '../market/format';
import { centsToPx, economics } from '../ticket/order';
import { feeToPercent, feeToRate, type BuilderConfig } from '../builder/builder';
import { add, decToString, parseDec, ZERO } from '../trading/decimal';
import { BUILDER_FEE_RATE, outcomeFee, rateToPct, SPOT_BASE_MAKER_RATE, SPOT_BASE_TAKER_RATE } from '../trading/fees';
import { OUTCOME_RULES, validatePrice } from '../trading/rules';
import { settlementResult } from './calc';
import { delocalizeInput, localizeNumber } from '../i18n/format';
import { useT } from '../i18n/useT';

type ScaleChoice = 'none' | '1.0' | 'example';

export interface FeeBreakdownProps {
  side: Side;
  /** Calculator buy price (wire) and shares; null when the calculator is invalid. */
  px: string | null;
  shares: string;
  /** deployerFeeScale of the live example, if any. */
  exampleScale?: string | null;
  exampleTitle?: string;
  /** Configured builder (builderConfigFor), null/undefined = builder codes off (the default). */
  builder?: BuilderConfig | null;
}

function Row({ label, value, note, testId }: { label: string; value: string; note?: string; testId?: string }) {
  return (
    <tr className="border-t border-border/60 align-top">
      <th scope="row" className="py-1.5 pe-3 text-start font-normal">
        {label}
        {note && <span className="block text-[11px] text-muted">{note}</span>}
      </th>
      <td className="num py-1.5 text-end" data-testid={testId}>
        {value}
      </td>
    </tr>
  );
}

export default function FeeBreakdown({ side, px, shares, exampleScale, exampleTitle, builder }: FeeBreakdownProps) {
  const exitId = useId();
  const t = useT();
  const [scale, setScale] = useState<ScaleChoice>('1.0');
  const [taker, setTaker] = useState(true);
  const [exitCents, setExitCents] = useState('70');
  const sideName = t(side === 0 ? 'common.yes' : 'common.no');
  const scaleValue = scale === 'none' ? null : scale === '1.0' ? '1.0' : (exampleScale ?? null);

  const buy = px ? economics(px, shares, true) : null;
  const builderFeeRate = builder ? feeToRate(builder.fee) : BUILDER_FEE_RATE;
  const buyFee = buy ? outcomeFee({ event: 'buy', value: buy.notional, deployerFeeScale: scaleValue, builderFeeRate }) : null;

  const exitPx = centsToPx(exitCents);
  const exitOk = exitPx !== null && validatePrice(exitPx, OUTCOME_RULES).ok;
  const sell = buy && exitOk ? economics(exitPx, shares, false) : null;
  const sellFee = sell
    ? outcomeFee({ event: 'sell', value: sell.notional, taker, deployerFeeScale: scaleValue, builderFeeRate })
    : null;
  // Builder fee for the round trip: the buy plus (when a sell price is set) the sell.
  const builderTotal = decToString(
    add(parseDec(buyFee?.builderFee ?? '0') ?? ZERO, parseDec(sellFee?.builderFee ?? '0') ?? ZERO),
  );

  const win = buy ? settlementResult(side, shares, side === 0 ? '1' : '0', buy.notional, scaleValue) : null;
  const multiplier = (sellFee ?? buyFee)?.multiplier;

  if (!buy) {
    return <p className="text-sm text-muted">{t('learn.fees.enter_valid')}</p>;
  }

  return (
    <div className="space-y-3" data-testid="fee-breakdown">
      <div className="flex flex-wrap items-end gap-3 text-xs">
        <div role="radiogroup" aria-label={t('learn.fees.scale_group')} className="flex flex-wrap gap-1 rounded-lg bg-surface-2/50 p-1">
          {(
            [
              ['none', t('learn.fees.scale_none')],
              ['1.0', t('learn.fees.scale_one')],
              ...(exampleScale !== undefined ? [['example', t('learn.fees.scale_example')] as const] : []),
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={scale === k}
              onClick={() => setScale(k as ScaleChoice)}
              title={k === 'example' && exampleTitle ? t('learn.fees.example_title', { title: exampleTitle }) : undefined}
              className={`rounded-md px-2.5 py-1 font-medium ${scale === k ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div role="radiogroup" aria-label={t('learn.fees.sell_type')} className="flex gap-1 rounded-lg bg-surface-2/50 p-1">
          {([true, false] as const).map((tk) => (
            <button
              key={String(tk)}
              type="button"
              role="radio"
              aria-checked={taker === tk}
              onClick={() => setTaker(tk)}
              className={`rounded-md px-2.5 py-1 font-medium ${taker === tk ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'}`}
            >
              {tk ? t('learn.fees.taker') : t('learn.fees.maker')}
            </button>
          ))}
        </div>
        <label htmlFor={exitId} className="text-muted">
          {t('learn.fees.sell_price')}
          <input
            id={exitId}
            inputMode="decimal"
            value={exitCents}
            onChange={(e) => setExitCents(delocalizeInput(e.target.value))}
            className="num ms-2 w-20 rounded-md border border-border bg-bg px-2 py-1 text-sm text-fg"
            data-testid="fee-exit-price"
          />
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="fee-table">
          <caption className="pb-1 text-start text-xs text-muted">
            {t('learn.fees.caption', {
              shares: formatShares(shares),
              side: sideName,
              px: pxToCents(px!),
              mult: localizeNumber(multiplier?.factor ?? '1'),
            })}
          </caption>
          <tbody>
            <Row
              label={t('learn.fees.buy_label', { shares: formatShares(shares), side: sideName })}
              value={formatUsd(buyFee?.hlFee ?? '0', 4)}
              note={t('learn.fees.buy_note', { value: formatUsd(buy.notional) })}
              testId="fee-buy"
            />
            <Row
              label={t('learn.fees.sell_label', { px: exitOk ? pxToCents(exitPx!) : '—' })}
              value={sellFee ? formatUsd(sellFee.hlFee, 4) : '—'}
              note={
                sellFee
                  ? t(taker ? 'learn.fees.sell_note_taker' : 'learn.fees.sell_note_maker', {
                      pct: localizeNumber(rateToPct(sellFee.rate)),
                      value: formatUsd(sell!.notional),
                    })
                  : t('learn.fees.sell_enter')
              }
              testId="fee-sell"
            />
            <Row
              label={t('learn.fees.settle_label', { side: sideName })}
              value={win ? formatUsd(win.fee, 4) : '—'}
              note={win ? t('learn.fees.settle_note', { value: formatUsd(win.value) }) : undefined}
              testId="fee-settle"
            />
            <Row
              label={t('learn.fees.builder_label')}
              value={formatUsd(builderTotal, 4)}
              note={
                builder
                  ? t('learn.fees.builder_note', {
                      pct: localizeNumber(feeToPercent(builder.fee)),
                      buy: formatUsd(buyFee?.builderFee ?? '0', 4),
                      sell: formatUsd(sellFee?.builderFee ?? '0', 4),
                    })
                  : t('learn.fees.builder_none')
              }
              testId="fee-builder"
            />
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted">
        {t('learn.fees.rates', {
          taker: localizeNumber(rateToPct(SPOT_BASE_TAKER_RATE)),
          maker: localizeNumber(rateToPct(SPOT_BASE_MAKER_RATE)),
        })}
      </p>
    </div>
  );
}
